import { useEffect, useMemo, useRef } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Building2,
  Clock,
  KeyRound,
  Loader2,
  Settings,
  Shield,
  ShieldCheck,
  ShieldOff,
  User,
  UserCheck,
  UserMinus,
  UserPlus,
  type LucideIcon,
} from "lucide-react";
import { Skeleton } from "@nexus/ui";
import { cn } from "@nexus/ui";
import {
  userAuditLogInfiniteQueryOptions,
  accessAuditLogInfiniteQueryOptions,
  tenantAuditLogInfiniteQueryOptions,
} from "@/features/identity-hub/lib/queries/audit-log.queries";
import { relativeTimeAudit, relativeTimeI18n } from "@/features/identity-hub/utils/time";
import type { AuditLogItem } from "@/features/identity-hub/api/audit-log.api";

import type { TFunction } from "i18next";
import type { AuditActionValue } from "@nexus/contracts";

// ─── Types ────────────────────────────────────────────────────────────────────

export type TimelineScope = "user" | "access" | "tenant";

/**
 * `compact` — flat list with absolute timestamps (Person / Role drawers).
 * `grouped` — day-grouped list with per-action icon badges (Org drawer Audit tab).
 */
export type AuditTimelineVariant = "compact" | "grouped";

export type AuditTimelineSummary = { shown: number; hasMore: boolean };

interface AuditTimelineProps {
  scope: TimelineScope;
  scopeId: string;
  /** When false, renders a "no permission" state instead of fetching. */
  hasPermission: boolean;
  /** Called when a row is clicked — used to open the global audit modal focused on that entry. */
  onEntryClick?: (entryId: string) => void;
  /** Client-side substring filter on actor, target, and action (org drawer toolbar). */
  searchPrefill?: string;
  /** When set, only actions whose `action` starts with one of these prefixes are shown. */
  categoryFilter?: string[];
  className?: string;
  /**
   * Per-page size. Defaults to 20 — small enough to keep the drawer audit
   * tab cheap on first paint, large enough that the average user never has
   * to scroll. Increase only if the surface is taller than the drawer.
   */
  pageSize?: number;
  variant?: AuditTimelineVariant;
  /** Reports how many events are visible after filtering, e.g. for a drawer footer. */
  onSummaryChange?: (summary: AuditTimelineSummary) => void;
}

type IconTone = "neutral" | "success" | "error";

const ICON_TONE_CLASS: Record<IconTone, string> = {
  neutral: "bg-muted text-foreground/80",
  success: "bg-success-subtle text-success-subtle-foreground",
  error: "bg-error-subtle text-error",
};

const ACTION_ICON: Record<AuditActionValue, { icon: LucideIcon; tone: IconTone }> = {
  role_assigned:        { icon: ShieldCheck, tone: "success" },
  role_revoked:         { icon: UserMinus,   tone: "error" },
  role_replaced:        { icon: Shield,      tone: "neutral" },
  user_invited:         { icon: UserPlus,    tone: "neutral" },
  user_activated:       { icon: UserCheck,   tone: "neutral" },
  user_updated:         { icon: User,        tone: "neutral" },
  account_suspended:    { icon: UserMinus,   tone: "error" },
  account_reactivated:  { icon: UserCheck,   tone: "neutral" },
  org_created:          { icon: Building2,   tone: "neutral" },
  org_updated:          { icon: Settings,    tone: "neutral" },
  org_deactivated:      { icon: Building2,   tone: "error" },
  org_reactivated:      { icon: Building2,   tone: "neutral" },
  settings_imported:    { icon: Settings,    tone: "neutral" },
  perm_changed:         { icon: Shield,      tone: "neutral" },
  role_created:         { icon: Shield,      tone: "neutral" },
  role_updated:         { icon: Shield,      tone: "neutral" },
  role_deleted:         { icon: Shield,      tone: "error" },
  role_restored:        { icon: Shield,      tone: "neutral" },
  template_created:     { icon: Shield,      tone: "neutral" },
  template_updated:     { icon: Shield,      tone: "neutral" },
  template_archived:    { icon: Shield,      tone: "error" },
  template_restored:    { icon: Shield,      tone: "neutral" },
  template_applied_to_org: { icon: Shield,   tone: "success" },
  template_removed_from_org: { icon: Shield, tone: "error" },
  invite_created:       { icon: UserPlus,    tone: "neutral" },
  invite_regenerated:   { icon: UserPlus,    tone: "neutral" },
  invite_resent:        { icon: UserPlus,    tone: "neutral" },
  invite_revoked:       { icon: UserMinus,   tone: "error" },
  login:                { icon: KeyRound,    tone: "neutral" },
  mfa_enabled:          { icon: KeyRound,    tone: "neutral" },
  mfa_disabled:         { icon: KeyRound,    tone: "error" },
  ops_settings_updated: { icon: Settings,    tone: "neutral" },
  system_settings_updated: { icon: Settings, tone: "neutral" },
};

// ─── Shared scope-options builder ──────────────────────────────────────────────

// Each factory returns a different literal `queryKey` tuple
// ("user" | "access" | "tenant"), so the ternary's union type can't be
// narrowed against `useInfiniteQuery`'s overloads — but every branch produces
// the same data shape (`PaginatedAuditLogs`). We pin the call to the
// user-scope option type so consumers stay fully typed; the runtime queryKey
// is always correct because each branch is constructed by its own factory.
function auditLogScopeOptions(scope: TimelineScope, scopeId: string, pageSize: number) {
  return (
    scope === "user"   ? userAuditLogInfiniteQueryOptions(scopeId,   { pageSize }) :
    scope === "access" ? accessAuditLogInfiniteQueryOptions(scopeId, { pageSize }) :
                         tenantAuditLogInfiniteQueryOptions(scopeId, { pageSize })
  ) as ReturnType<typeof userAuditLogInfiniteQueryOptions>;
}

/**
 * Audit-tab badge count for the Person/Role/Org drawers — shares the same
 * infinite-query cache as `AuditTimeline` (below) so only the first page is
 * fetched here and no extra request is made once the tab is opened.
 *
 * Must be called unconditionally on every render, including when `scopeId`
 * is falsy (drawers render this before their `if (!entity) return null`
 * guard) — pass `enabled: false` rather than skipping the call.
 */
export function useAuditLogBadgeCount(
  scope: TimelineScope,
  scopeId: string | undefined,
  opts: { enabled: boolean },
): number | undefined {
  const { data } = useInfiniteQuery({
    ...auditLogScopeOptions(scope, scopeId ?? "", 20),
    enabled: opts.enabled && !!scopeId,
  });
  return data?.pages[0]?.items.length ?? undefined;
}

// ─── AuditTimeline ────────────────────────────────────────────────────────────

export function AuditTimeline({
  scope,
  scopeId,
  hasPermission,
  onEntryClick,
  searchPrefill,
  categoryFilter,
  className,
  pageSize = 20,
  variant = "compact",
  onSummaryChange,
}: AuditTimelineProps) {
  const { t, i18n } = useTranslation();
  const baseOptions = auditLogScopeOptions(scope, scopeId, pageSize);

  const {
    data,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({ ...baseOptions, enabled: hasPermission && !!scopeId });

  // Single sentinel watched by IntersectionObserver. Using IO instead of the
  // shared `useInfiniteScroll` hook means we don't need to plumb the parent
  // drawer's scroll container ref through three levels of components — the
  // observer just fires whenever the sentinel becomes visible inside its
  // nearest scrolling ancestor.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    if (!hasNextPage || isFetchingNextPage) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            void fetchNextPage();
            break;
          }
        }
      },
      { rootMargin: "200px 0px" },
    );
    io.observe(node);
    return () => io.disconnect();
    // `data` and the filters decide whether the sentinel renders in the list or in the
    // filtered-empty state, so either change can mount a new sentinel node.
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, data, searchPrefill, categoryFilter]);

  // Flatten paginated items once per page-change so child rows have stable
  // references between re-renders.
  const items: AuditLogItem[] = useMemo(() => {
    const flat = data?.pages.flatMap((page) => page.items) ?? [];
    const q = searchPrefill?.trim().toLowerCase();
    return flat.filter((item) => {
      if (categoryFilter?.length) {
        const matchesCategory = categoryFilter.some((prefix) => item.action.startsWith(prefix));
        if (!matchesCategory) return false;
      }
      if (!q) return true;
      const haystack = [
        item.action,
        item.actorDisplayName,
        item.actorEmail,
        item.targetName,
        item.targetEmail,
        JSON.stringify(item.metadata ?? {}),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [data, searchPrefill, categoryFilter]);

  const hasMore = !!hasNextPage;
  useEffect(() => {
    onSummaryChange?.({ shown: items.length, hasMore });
  }, [items.length, hasMore, onSummaryChange]);

  if (!hasPermission) {
    return (
      <div className={cn("flex flex-col items-center gap-2 py-10 text-center", className)}>
        <ShieldOff className="h-6 w-6 text-muted-foreground" />
        <p className="text-xs text-muted-foreground">
          {t("iam.auditLog.timeline.noPermission", "You don't have permission to view audit logs.")}
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={cn("flex flex-col gap-2 py-2", className)}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full rounded" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className={cn("flex flex-col items-center gap-2 py-10 text-center", className)}>
        <p className="text-xs text-muted-foreground">{t("iam.auditLog.timeline.loadError", "Failed to load audit history.")}</p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="text-xs font-medium text-foreground underline-offset-2 hover:underline"
        >
          {t("iam.auditLog.retry", "Retry")}
        </button>
      </div>
    );
  }

  // Sentinel — IntersectionObserver auto-fires fetchNextPage when this enters the
  // viewport. Also acts as the visible loading/end indicator so the user knows
  // whether more events are on the way.
  const sentinel = (
    <div
      ref={sentinelRef}
      className="flex items-center justify-center py-3 text-[11px] text-muted-foreground"
    >
      {isFetchingNextPage ? (
        <span className="inline-flex items-center gap-1.5">
          <Loader2 className="h-3 w-3 animate-spin" />
          {t("iam.auditLog.timeline.loadingMore", "Loading more…")}
        </span>
      ) : hasNextPage ? (
        <button
          type="button"
          onClick={() => void fetchNextPage()}
          className="font-medium text-foreground underline-offset-2 hover:underline"
        >
          {t("iam.auditLog.timeline.loadMore", "Load more")}
        </button>
      ) : null}
    </div>
  );

  if (items.length === 0) {
    const isFilterActive = !!(searchPrefill?.trim() || categoryFilter?.length);
    const hasLoadedEvents = (data?.pages[0]?.items.length ?? 0) > 0;
    const isSearchingOlder = isFilterActive && (hasNextPage || isFetchingNextPage);
    return (
      <div className={cn("flex flex-col items-center gap-2 py-10 text-center", className)}>
        <Clock className="h-6 w-6 text-muted-foreground" />
        <p className="text-xs text-muted-foreground">
          {isSearchingOlder
            ? t("iam.orgDrawer.auditTab.searchingOlder", "No matches in recent events yet — checking older ones.")
            : isFilterActive && hasLoadedEvents
              ? t("iam.orgDrawer.auditTab.noFilterMatch", "No events match your filters.")
              : t("iam.auditLog.timeline.empty", "No audit events yet.")}
        </p>
        {isSearchingOlder && sentinel}
      </div>
    );
  }

  const lastItemId = items[items.length - 1]?.id;
  const renderRow = (item: AuditLogItem) => (
    <AuditTimelineRow
      key={item.id}
      item={item}
      variant={variant}
      isLast={item.id === lastItemId}
      onClick={onEntryClick ? () => onEntryClick(item.id) : undefined}
    />
  );

  return (
    <div className={cn("flex flex-col", variant === "grouped" && "gap-5", className)}>
      {variant === "grouped"
        ? groupItemsByDay(items).map((group) => (
            <section key={group.key} className="flex flex-col gap-1" data-testid="audit-timeline-day-group">
              <h3 className="pb-1 font-mono text-[10px] uppercase leading-[1.4] tracking-[0.4px] text-muted-foreground">
                {dayHeading(group.date, t, i18n.language)}
              </h3>
              {group.items.map(renderRow)}
            </section>
          ))
        : items.map(renderRow)}

      {sentinel}
    </div>
  );
}

// ─── Day grouping ────────────────────────────────────────────────────────────

type DayGroup = { key: string; date: Date; items: AuditLogItem[] };

/** Items arrive newest-first, so consecutive runs of the same local day form a group. */
function groupItemsByDay(items: AuditLogItem[]): DayGroup[] {
  const groups: DayGroup[] = [];
  for (const item of items) {
    const date = new Date(item.createdAt);
    const key = date.toDateString();
    const current = groups[groups.length - 1];
    if (current?.key === key) current.items.push(item);
    else groups.push({ key, date, items: [item] });
  }
  return groups;
}

function dayHeading(date: Date, t: TFunction, locale: string): string {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return t("iam.auditLog.timeline.dayToday", "Today");
  if (date.toDateString() === yesterday.toDateString()) return t("iam.auditLog.timeline.dayYesterday", "Yesterday");
  return date.toLocaleDateString(locale, {
    day: "numeric",
    month: "short",
    year: date.getFullYear() === today.getFullYear() ? undefined : "numeric",
  });
}

// ─── Row ─────────────────────────────────────────────────────────────────────

function AuditTimelineRow({
  item,
  variant,
  isLast,
  onClick,
}: {
  item: AuditLogItem;
  variant: AuditTimelineVariant;
  isLast: boolean;
  onClick?: () => void;
}) {
  const { t } = useTranslation();
  const isGrouped = variant === "grouped";

  const actorName  = item.actorDisplayName || item.actorEmail || t("iam.auditLog.fallback.unknownActor");
  const meta       = item.metadata as Record<string, unknown>;
  const accessName = typeof meta["accessName"] === "string" ? meta["accessName"] : null;
  const tenantName = typeof meta["tenantName"] === "string" ? meta["tenantName"] : null;

  const verb       = t(`iam.auditLog.timeline.verb.${item.action}`, { defaultValue: item.action.replace(/_/g, " ") });

  // For create/update/delete-role and org-* events, the targetName IS the
  // subject (the role or org). For grant/revoke/invite, the role lives in
  // metadata while targetName is the affected user.
  const targetIsRole =
    item.action === "role_deleted" ||
    item.action === "role_created" ||
    item.action === "role_updated" ||
    item.action === "template_applied_to_org" ||
    item.action === "template_removed_from_org";
  const targetIsOrg  = item.action === "org_created"  || item.action === "org_updated"  || item.action === "org_deactivated";

  // For role_replaced, snapshot both role names so we can render "Editor → Admin".
  const fromAccessName = typeof meta["fromAccessName"] === "string" ? meta["fromAccessName"] : null;
  const toAccessName   = typeof meta["toAccessName"]   === "string" ? meta["toAccessName"]   : null;

  // What gets bolded right after the verb.
  const subject = (() => {
    if (item.action === "role_assigned" || item.action === "role_revoked") return accessName ?? t("iam.auditLog.fallback.aRole");
    if (item.action === "role_replaced") {
      if (fromAccessName && toAccessName) return `${fromAccessName} → ${toAccessName}`;
      return toAccessName ?? fromAccessName ?? t("iam.auditLog.fallback.aRole");
    }
    if (targetIsRole) return item.targetName ?? t("iam.auditLog.fallback.aRole");
    if (targetIsOrg)  return item.targetName ?? t("iam.auditLog.fallback.anOrg");
    if (item.action === "invite_created") return accessName ?? t("iam.auditLog.fallback.aRole");
    if (item.action === "perm_changed")   return item.targetName ?? t("iam.auditLog.fallback.aRole");
    return null;
  })();

  const showSubject = !!subject;
  const targetPreposition =
    item.action === "role_revoked"  ? t("iam.auditLog.timeline.prepositionFrom", "from ") :
    item.action === "role_replaced" ? t("iam.auditLog.timeline.prepositionOn", "on ")   :
    t("iam.auditLog.timeline.prepositionTo", "to ");

  // The secondary line shows the user the action affected, but only when that
  // user is a separate entity from the bolded subject above. For role/org
  // events the subject IS the target — there's no second person to mention.
  const showSecondaryTarget =
    !!item.targetName &&
    item.targetName !== actorName &&
    !targetIsRole &&
    !targetIsOrg;

  const Wrapper = onClick ? "button" : "div";
  const hasKnownActor = !!(item.actorDisplayName || item.actorEmail);
  const actionIcon = ACTION_ICON[item.action];

  return (
    <Wrapper
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={cn(
        "group/row flex w-full items-start gap-3 py-2.5 text-left",
        !isLast && (isGrouped ? "border-b border-border-subtle" : "border-b border-border/50"),
        onClick && "-mx-2 rounded-md px-2 transition-colors hover:bg-muted/50",
      )}
    >
      {isGrouped && actionIcon && (
        <span
          className={cn("flex size-7 shrink-0 items-center justify-center rounded-full", ICON_TONE_CLASS[actionIcon.tone])}
          aria-hidden
        >
          <actionIcon.icon className="size-3.5" />
        </span>
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className={cn("leading-snug", isGrouped ? "text-[13px]" : "text-[12.5px]")}>
          <span className={cn("font-medium", hasKnownActor ? "text-foreground" : "text-muted-foreground italic")}>
            {actorName}
          </span>
          {" "}
          <span className="text-muted-foreground">{verb}</span>
          {showSubject && (
            <>
              {" "}
              <span className="font-medium text-foreground">{subject}</span>
            </>
          )}
        </span>
        <span className="text-[11px] text-muted-foreground">
          {showSecondaryTarget && (
            <>
              <span className="text-muted-foreground/70">{showSubject ? targetPreposition : ""}</span>
              <span className="text-foreground/80">{item.targetName}</span>
              {item.targetEmail && (
                <span className="text-muted-foreground/60"> ({item.targetEmail})</span>
              )}
            </>
          )}
          {tenantName && (
            <>
              {showSecondaryTarget ? " · " : ""}
              <span className="text-muted-foreground/70">{t("iam.auditLog.timeline.inLabel", "in ")}</span>
              <span className="text-foreground/80">{tenantName}</span>
            </>
          )}
        </span>
      </div>
      <span className={cn("shrink-0 font-mono text-[11px] text-muted-foreground", isGrouped && "pt-1")}>
        {isGrouped ? relativeTimeI18n(item.createdAt, t) : relativeTimeAudit(item.createdAt)}
      </span>
    </Wrapper>
  );
}
